import { useRef, useMemo, useEffect } from 'react'

import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useThrottledTracker } from 'src/lib/js/hooks/useTracker'
import { TrackingEvents } from 'src/shared/components/common/tracking/TrackingEvents'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import { useCustomer } from 'src/public/components/online_ordering/CustomerContextCommon'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'
import { useLoyalty } from 'src/public/components/online_ordering/LoyaltyContext'
import { useOffers } from 'src/public/components/online_ordering/OffersContext'
import { useTimeBasedRules } from 'src/public/components/online_ordering/TimeBasedRuleContext'
import { useOOMenus } from 'src/public/components/online_ordering/useOOMenu'
import { useExperimentUserId } from 'src/shared/components/common/ab_testing/ABTestContext'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { getAuthenticationStatus } from 'public/components/default_template/online_ordering/checkout/checkoutUtils'
import { getTargetSegmentNames } from 'public/components/default_template/online_ordering/cart/offers/OfferUtils'

/**
 * @param skip - additional flag to optionally skip tracking the order page view event.
 */
export const useTrackOrderPage = (skip: boolean = false) => {
  const { track } = useThrottledTracker()

  const {
    ooRestaurant: restaurant,
    diningOptions,
    ooPromoBanners
  } = useRestaurant()
  const { timeBasedRulesMap, loading: timeBasedRulesLoading } =
    useTimeBasedRules()
  const {
    rankedPromoOfferDiscounts,
    loading: offersLoading,
    offerFromUrl
  } = useOffers()
  const orderPageViewRef = useRef(false)
  const { hasLoyaltyAccount } = useLoyalty()
  const { deliveryEnabled, selectedDiningOptionBehavior } = useFulfillment()
  const { cart, cartGuid } = useCart()
  const { fulfillmentData } = useFulfillment()
  const { loading: menusLoading, menus, menuItems } = useOOMenus({})
  const userId = useExperimentUserId()
  const { customer, loadingCustomer } = useCustomer()

  // Helper function to extract all menu items from the menu structure
  const allMenuItems = useMemo(() => {
    if (menuItems !== undefined && menuItems !== null) {
      return menuItems
    }
    if (!menus) return []
    return menus.flatMap(
      (menu) => menu.groups?.flatMap((group) => group?.items || []) || []
    )
  }, [menus, menuItems])

  const menuItemsWithImagesPercentage = useMemo(() => {
    if (!allMenuItems || allMenuItems.length === 0) {
      return 0
    }

    const itemsWithImages = allMenuItems.filter(
      (item) =>
        item &&
        item.imageUrls !== undefined &&
        item.imageUrls !== null &&
        Object.keys(item.imageUrls).length > 0
    ).length
    return Math.round((itemsWithImages / allMenuItems.length) * 100)
  }, [allMenuItems])

  const loyaltyStatus = useMemo(() => {
    if (restaurant?.loyaltyConfig?.programName) {
      if (customer) {
        return hasLoyaltyAccount
          ? 'Authenticated and a rewards member'
          : 'Authenticated and not a rewards member'
      }
      return 'Guest'
    }
    return undefined
  }, [customer, hasLoyaltyAccount, restaurant?.loyaltyConfig])

  const { RxASAPDeliveryQuoteTime, RxASAPPickupQuoteTime } = useMemo(() => {
    const deliveryOption = diningOptions?.find(
      (option) => option.behavior === DiningOptionBehavior.Delivery
    )
    const takeoutOption = diningOptions?.find(
      (option) => option.behavior === DiningOptionBehavior.TakeOut
    )
    return {
      RxASAPDeliveryQuoteTime: deliveryOption?.asapSchedule?.availableNow
        ? restaurant?.minimumDeliveryTime
        : null,
      RxASAPPickupQuoteTime: takeoutOption?.asapSchedule?.availableNow
        ? restaurant?.minimumTakeoutTime
        : null
    }
  }, [
    diningOptions,
    restaurant?.minimumDeliveryTime,
    restaurant?.minimumTakeoutTime
  ])

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !orderPageViewRef.current &&
      !timeBasedRulesLoading &&
      !offersLoading &&
      !menusLoading &&
      !loadingCustomer &&
      diningOptions &&
      !skip
    ) {
      track(TrackingEvents.ORDER_PAGE_VIEW, {
        cartGuid: cartGuid,
        isAsapAvailable: !!diningOptions.some(
          (option) => option.asapSchedule?.availableNow
        ),
        isScheduledAvailable: !!diningOptions.some(
          (option) => option.futureSchedule?.dates?.length
        ),
        leadTimeRuleCount: Object.values(timeBasedRulesMap).filter(
          (rules) => rules.leadTimeRule
        ).length,
        preorderRuleCount: Object.values(timeBasedRulesMap).filter(
          (rules) => rules.preorderRule
        ).length,
        userExperimentId: userId,
        hasOOEntitlement: restaurant?.hasOnlineOrderingModule ?? true,
        hasToastLocalEntitlement: restaurant?.hasToastLocalModule ?? true,
        numOffersPresent: rankedPromoOfferDiscounts?.length ?? 0,
        isDeliveryThresholdOfferPresent: Boolean(
          ooPromoBanners?.deliveryBanners?.length ?? 0
        ),
        isDeliveryAvailable: deliveryEnabled,
        loyaltyProgramStatus: loyaltyStatus,
        diningOption: selectedDiningOptionBehavior,
        deliveryProvider: cart?.deliveryProviderInfo?.provider,
        menuNavExperience:
          (allMenuItems ?? []).length > 1500 ? 'Paginated' : 'Non-paginated',
        RxASAPDeliveryQuoteTime,
        RxASAPPickupQuoteTime,
        promoCodeInUrl: offerFromUrl?.promoCode,
        menuItemsWithImagesPercentage: menuItemsWithImagesPercentage,
        validDeliveryAddressPresent:
          !!fulfillmentData?.cartFulfillmentData?.deliveryInfo,
        authStatusOnOPV: getAuthenticationStatus(customer),
        targetSegments: getTargetSegmentNames(rankedPromoOfferDiscounts)
      })
      orderPageViewRef.current = true
    }
    // diningOptions, timeBasedRulesMap, and rankedPromoOfferDiscounts get set when the data finishes loading
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    skip,
    diningOptions,
    timeBasedRulesMap,
    restaurant?.hasOnlineOrderingModule,
    restaurant?.hasToastLocalModule,
    rankedPromoOfferDiscounts,
    ooPromoBanners,
    timeBasedRulesLoading,
    offersLoading,
    loadingCustomer,
    customer,
    deliveryEnabled,
    RxASAPDeliveryQuoteTime,
    RxASAPPickupQuoteTime,
    offerFromUrl,
    menuItemsWithImagesPercentage,
    fulfillmentData?.cartFulfillmentData?.deliveryInfo
  ])
}
